import {
  Cake,
  CalendarCheck,
  CalendarDays,
  ClipboardCheck,
  DoorOpen,
  FileSignature,
  FileStack,
  FileText,
  IdCard,
  Shirt,
} from "lucide-react"
import { useEffect } from "react"
import { useNavigate, useParams } from "react-router"

import { Cumpleanos } from "@/componentes/cumpleanos/Cumpleanos"
import { ListaEvaluaciones } from "@/componentes/evaluaciones/ListaEvaluaciones"
import { DotacionTH } from "@/componentes/talento/DotacionTH"
import { IngresosTH } from "@/componentes/talento/IngresosTH"
import { ListaDocumentosTH } from "@/componentes/talento/ListaDocumentosTH"
import { PermisosTH } from "@/componentes/talento/PermisosTH"
import { PlantillasTH } from "@/componentes/talento/PlantillasTH"
import { AgendaLaboral } from "@/componentes/talento/planificacion/AgendaLaboral"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { Personas } from "@/paginas/admin/Personas"
import { Contratos } from "@/paginas/interno/nomina/Contratos"

const VISTAS = [
  { valor: "personas", etiqueta: "Personas", icono: IdCard, permiso: "persona:ver" },
  { valor: "ingresos", etiqueta: "Ingresos", icono: DoorOpen, permiso: "persona:ver" },
  { valor: "contratos", etiqueta: "Contratos", icono: FileSignature, permiso: "nomina:ver" },
  { valor: "documentos", etiqueta: "Documentos", icono: FileText, permiso: "documento_th:ver" },
  { valor: "permisos", etiqueta: "Permisos", icono: CalendarCheck, permiso: "documento_th:ver" },
  { valor: "dotacion", etiqueta: "Dotación", icono: Shirt, permiso: "dotacion:ver" },
  {
    valor: "evaluaciones",
    etiqueta: "Evaluaciones",
    icono: ClipboardCheck,
    permiso: "evaluacion:ver",
  },
  {
    valor: "planificacion",
    etiqueta: "Planificación",
    icono: CalendarDays,
    permiso: "th_calculo:ver",
  },
  {
    valor: "plantillas",
    etiqueta: "Plantillas",
    icono: FileStack,
    permiso: "plantilla_th:administrar",
  },
  { valor: "cumpleanos", etiqueta: "Cumpleaños", icono: Cake, permiso: "cumpleanos:ver" },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

const SUBTITULO: Record<Vista, string> = {
  personas: "Quién trabaja en la agencia: su ficha, sus datos y su carnet.",
  ingresos: "En qué paso del ingreso va cada trabajador, y su hoja de ruta del puesto.",
  contratos: "Quién cobra cuánto, cada cuánto, y a qué ficha se le paga.",
  documentos: "Constancias, contratos, acuerdos y solicitudes, llenos con la ficha del empleado.",
  permisos:
    "Quién pidió permiso, cuántos días hábiles, qué falta por resolver y cuánto lleva cada uno.",
  dotacion:
    "Uniformes, equipos, herramientas e insumos entregados a cada trabajador, y lo devuelto.",
  evaluaciones: "La evaluación del desempeño: autoevaluación, Dirección, sesión 1 a 1 y PDI.",
  planificacion: "Vacaciones, garantía y utilidades del personal, según su fecha de ingreso.",
  plantillas: "Los documentos que la agencia sabe generar, con sus datos entre llaves.",
  cumpleanos: "Quién cumple pronto, y su tarjeta: la foto sin fondo y en primer plano.",
}

/**
 * Talento humano: el departamento, con sus vistas en pestañas.
 *
 * ⚠ **Nace de partir en dos la relación con el empleado**, que es la decisión de la
 * agencia: lo LABORAL —quién está contratado, con qué sueldo y desde cuándo— es de
 * aquí, y lo FINANCIERO —qué se le paga este mes, con qué conceptos— es de
 * «Administración». Antes los contratos eran una pestaña de «Sueldos y salarios», que
 * mezclaba las dos mitades en un sitio que llevaba administración.
 *
 * ⚠ **«Personas» deja de ser entrada suelta y entra aquí**, que es su departamento.
 * `/personas` sigue llevando a su pestaña: hay enlaces guardados, y la ficha de
 * usuario enlaza a ella.
 *
 * ⚠ **Dos llaves, a propósito**, como en Tesorería y el Catálogo: `talento_humano:ver`
 * ofrece la entrada y cada pestaña sigue pidiendo la suya —ver una ficha y ver lo que
 * gana esa persona son decisiones distintas, y por eso contratos pide `nomina:ver`—.
 *
 * ⚠ **Es el sitio donde irán cargos, vacaciones y prestaciones** cuando toquen. El
 * blueprint deja la nómina legal venezolana entera fuera de alcance (LOTTT, IVSS,
 * FAOV); esto no la adelanta, sólo deja de esconder lo laboral dentro de finanzas.
 */
export function TalentoHumano() {
  const usuario = usarSesion((e) => e.usuario)
  const { vista: enRuta } = useParams()
  const navegar = useNavigate()

  const visibles = VISTAS.filter((v) => tienePermiso(usuario, v.permiso))
  const vista: Vista | undefined =
    visibles.find((v) => v.valor === enRuta)?.valor ?? visibles[0]?.valor

  useEffect(() => {
    if (vista !== undefined && enRuta !== vista) {
      navegar(`/talento-humano/${vista}`, { replace: true })
    }
  }, [enRuta, vista, navegar])

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Talento humano</h1>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {vista ? SUBTITULO[vista] : "La gente de la agencia y su relación laboral."}
        </p>
      </header>

      {vista === undefined ? (
        <p className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
          No tienes acceso a ninguna de sus vistas. Cada una pide su propio permiso, y se conceden
          desde «Roles y permisos».
        </p>
      ) : (
        <>
          <Pestanas
            pestanas={visibles}
            activa={vista}
            onCambiar={(v) => navegar(`/talento-humano/${v}`, { replace: true })}
            etiqueta="Vistas de talento humano"
            idPanel="panel-talento-humano"
            prefijoId="pestana-talento-humano"
          />
          <div
            id="panel-talento-humano"
            role="tabpanel"
            aria-labelledby={`pestana-talento-humano-${vista}`}
            className="mt-4"
          >
            {vista === "personas" && <Personas />}
            {vista === "contratos" && <Contratos />}
            {vista === "ingresos" && <IngresosTH />}
            {vista === "documentos" && <ListaDocumentosTH modo="th" />}
            {vista === "permisos" && <PermisosTH />}
            {vista === "evaluaciones" && <ListaEvaluaciones />}
            {vista === "dotacion" && <DotacionTH />}
            {vista === "planificacion" && <AgendaLaboral />}
            {vista === "plantillas" && <PlantillasTH />}
            {vista === "cumpleanos" && <Cumpleanos />}
          </div>
        </>
      )}
    </div>
  )
}
